import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Download, FileText, ShoppingCart, ExternalLink, BookOpen
} from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EbookCard = ({ ebook, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
      data-testid={`ebook-card-${ebook.id}`}
    >
      <div className="relative rounded-2xl overflow-hidden glass-light card-hover">
        {/* Cover Image */}
        <div className="aspect-[3/4] relative overflow-hidden bg-muted">
          <img
            src={ebook.cover_image}
            alt={ebook.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          
          {/* Format Badge */}
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/80 text-white flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {ebook.format}
            </span>
          </div>

          {/* Price Badge */}
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 rounded-full text-sm font-bold bg-cta text-background">
              {ebook.price}
            </span>
          </div>
        </div>
        
        {/* Info */}
        <div className="p-6">
          <h3 className="text-xl font-serif font-semibold text-white mb-2 line-clamp-2">
            {ebook.title}
          </h3>
          <p className="text-sm text-secondary mb-2">{ebook.author}</p>
          
          {ebook.pages && (
            <p className="text-xs text-muted-foreground mb-3">{ebook.pages}</p>
          )}

          <p className="text-sm text-muted-foreground mb-6 line-clamp-3">
            {ebook.description}
          </p>
          
          <div className="space-y-3">
            <PurchaseButton
              item={ebook}
              itemType="ebook"
              className="w-full flex items-center justify-center gap-2"
            />
            {(ebook.download_link || ebook.purchase_link) && (
              <a
                href={ebook.download_link || ebook.purchase_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                data-testid={`download-ebook-${ebook.id}`}
              >
                <Download className="w-4 h-4" />
                Download / View
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function EbooksPage() {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ebooks`)
      .then(res => {
        setEbooks(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const featured = ebooks.filter(e => e.is_featured);
  const regular = ebooks.filter(e => !e.is_featured);

  return (
    <div className="min-h-screen pt-24" data-testid="ebooks-page">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium">Digital Resources</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="ebooks-title">
              Downloadable E-Books
            </h1>
            <p className="text-xl text-muted-foreground">
              Comprehensive guides and resources on VR education, cultural heritage, and innovative teaching methods.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured E-Books */}
      {featured.length > 0 && (
        <section className="py-16">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-serif font-bold text-white mb-8"
            >
              Featured E-Books
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((ebook, index) => (
                <EbookCard key={ebook.id} ebook={ebook} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All E-Books */}
      {regular.length > 0 && (
        <section className="py-16 bg-card/50">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-serif font-bold text-white mb-8"
            >
              More E-Books
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regular.map((ebook, index) => (
                <EbookCard key={ebook.id} ebook={ebook} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {ebooks.length === 0 && !loading && (
        <div className="container-custom py-16 text-center">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground">E-books coming soon!</p>
        </div>
      )}

      {/* Also Check Out */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-serif font-bold text-white mb-6">
              Also Check Out Our Books
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Explore our children's book series featuring Jason's adventures with learning disabilities, cultural exploration, and VR.
            </p>
            <Link to="/books" className="btn-secondary inline-flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Browse Children's Books
            </Link>
          </motion.div>
        </div>
      </section>

      {loading && (
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl glass-light animate-pulse">
                <div className="aspect-[3/4] bg-muted" />
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-20 bg-muted rounded" />
                  <div className="h-12 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
